import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Plus, Users, X } from 'lucide-react';

// Create a character or location straight from the workspace tabs — it still needs a home project.
export function WorkspaceCreateDialog({ kind, projects, onCreated, onClose }) {
  const isChar = kind === 'character';
  const [projectId, setProjectId] = useState(projects[0]?.id || '');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState('main');
  const [busy, setBusy] = useState(false);
  const Icon = isChar ? Users : MapPin;

  const submit = async (e) => {
    e.preventDefault();
    if (!projectId) { toast.error('Choose a project'); return; }
    if (!name.trim()) { toast.error(`Give the ${kind} a name`); return; }
    setBusy(true);
    try {
      const body = isChar ? { name: name.trim(), description: desc.trim(), character_type: type, tags: [] } : { name: name.trim(), description: desc.trim() };
      const res = await api.post(`/projects/${projectId}/${isChar ? 'characters' : 'locations'}`, body);
      onCreated({ ...res.data, project_name: projects.find(p => p.id === projectId)?.name || '' });
      toast.success(`${res.data.name} created`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || `Could not create ${kind}`); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4" data-testid={`workspace-create-${kind}`} onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#0E0E12] border border-white/10 rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#FF4500]" /> New {kind}</p>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]" data-testid="workspace-create-close"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Project</label>
          <select data-testid="workspace-create-project" value={projectId} onChange={e => setProjectId(e.target.value)} className="ff-input w-full text-sm mt-1">
            {projects.length === 0 && <option value="">No projects yet — create one first</option>}
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <p className="text-[10px] text-[#55555C] mt-1">Every {kind} lives in a project; you can reuse it in any other project afterwards.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Name</label>
          <input data-testid="workspace-create-name" value={name} onChange={e => setName(e.target.value)} className="ff-input w-full text-sm mt-1" placeholder={isChar ? 'e.g. Detective Mara Quinn' : 'e.g. Harbour warehouse — night'} autoFocus />
        </div>
        {isChar && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Type</label>
            <div className="flex gap-1.5 mt-1">
              {['main', 'background', 'extra'].map(t => (
                <button type="button" key={t} data-testid={`workspace-create-type-${t}`} onClick={() => setType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border ${type === t ? 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06]'}`}>{t}</button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Description</label>
          <textarea data-testid="workspace-create-desc" value={desc} onChange={e => setDesc(e.target.value)} rows={4} className="ff-input w-full text-xs mt-1 resize-none"
            placeholder={isChar ? 'Appearance, age, wardrobe…' : 'Architecture, light, weather, era…'} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs text-[#8A8A93] hover:text-white px-3">Cancel</button>
          <button type="submit" data-testid="workspace-create-submit" disabled={busy || !projectId} className="ff-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create {kind}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
